import { FiX, FiSettings } from "react-icons/fi";
import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { Item } from "types/common";
import { getTagData } from "api/tags";
import FourLineChart from "../Charts/FourLinesChart";
import classNames from "utilities/ClassNames";

type BlockData = {
  id: number;
  voltage: string;
  ohmic: string;
  temperature: string;
};

const Table = ({ data }: { data: BlockData[] }) => {
  return (
    <div className="w-full h-full max-h-full rounded-lg border border-[#134366] bg-[#1D4A7A] p-1">
      {/* Header */}
      <div className="grid grid-cols-4 text-white font-bold bg-[#1A3A5F] p-1 xl-2xl:p-2 rounded-t-lg">
        <div>Blocks ID</div>
        <div>Voltage</div>
        <div>Ohmic</div>
        <div>Temperature</div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-1 mt-1 h-full max-h-full">
        {data.map((row, idx) => (
          <div
            key={row.id}
            className={classNames(
              'grid grid-cols-4 w-full px-2 py-0 xl-2xl:py-0.5 2xl:py-1 text-white rounded-xl',
              (idx === 2 || idx === 8)
                ? 'bg-[#FDBD4D]/40 border border-[#FDBD4D]'
                : idx === 15
                  ? 'bg-[#FF5233]/40 border border-[#FF5233]'
                  : 'bg-[#175E92]'
            )}
          >
            <div>{row.id}</div>
            <div>{row.voltage}</div>
            <div>{row.ohmic}</div>
            <div>{row.temperature}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const UPSModal = ({ chartData, setShowModal, onEdit, title }: { chartData: any, setShowModal: Dispatch<SetStateAction<boolean>>, onEdit?: any, title?: string }) => {

  const [randomTag, setRandomTag] = useState<Item | null>(null);
  const getSingleTag = async () => {
    const randomTagData = await getTagData([244]);
    setRandomTag(randomTagData.data[0]);
  };

  useEffect(() => {
    getSingleTag();
  }, []);

  const blockData: BlockData[] = Array.from({ length: 19 }).map((_, i) => ({
    id: i + 1,
    voltage: '3.5 V',
    ohmic: '0.8 MoM',
    temperature: '31.C',
  }));

  return (
    <div className="h-screen w-screen bg-black/65 flex items-center justify-center fixed left-0 top-0 z-50"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="h-[90%] max-w-[1100px] xl-2xl:max-w-[1200px] 2xl:max-w-[1300px] w-full bg-black px-3 py-2 flex flex-col gap-2 overflow-hidden">
        <div className="bg-dark/85 relative w-full p-1 px-2 rounded flex h-max">
          <h2 className="2xl:text-2xl w-full text-center">{title ? title : 'UPS 1-String 1-43 Blocks'}</h2>
          <div className="absolute right-2 h-full top-0 flex items-center justify-center">
            <div className="flex gap-2">
              <button
                className="cursor-pointer bg-blue rounded-md p-0.5 2xl:p-1"
                onClick={() => setShowModal(false)}
              >
                <FiX size={20} />
              </button>
              {onEdit &&
                <button
                  className="cursor-pointer bg-blue rounded-md p-0.5 2xl:p-1"
                  onClick={() => onEdit()}
                >
                  <FiSettings size={20} />
                </button>
              }
            </div>
          </div>
        </div>
        <div className="flex gap-2 w-full flex-1 overflow-hidden h-full">
          <div className="h-full flex-1 bg-dark rounded-md">
            <div className="h-full p-2">
              <Table data={blockData} />;
            </div>
          </div>
          <div className="w-[35%] border border-[#134366] grid grid-rows-3 gap-2 rounded-lg bg-black">
            <div className="bg-dark/85 p-4 flex flex-col items-center justify-center w-full mx-auto">
              <h3 className="text-center font-bold w-full">Block Voltage</h3>
              <FourLineChart data={chartData} className="w-full h-40" />
            </div>
            <div className="bg-dark/85 p-4 flex flex-col items-center justify-center w-full mx-auto">
              <h3 className="text-center font-bold w-full">Block Ohmic</h3>
              <FourLineChart data={chartData} className="w-full h-40" />
            </div>
            <div className="bg-dark/85 p-4 flex flex-col items-center justify-center w-full mx-auto">
              <h3 className="text-center font-bold w-full">Block Temperature</h3>
              <FourLineChart data={chartData} className="w-full h-40" />
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default UPSModal;